import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CircularProgress, Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Sidebar from '../Sidebar/Sidebar';
import useAuth from '../../hooks/useAuth';
import { getIframeEmbedConfig } from '../../services/iframeEmbedService';

// We now pass 'resource' and 'params' instead of a static 'url'
const SecureEmbedPage = ({ resource, params = {} }) => {
  const { t } = useTranslation();
  const { isLoggedIn, user } = useAuth();
  
  const [embedData, setEmbedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  // 1. Extract to a stable string variable outside the hook to satisfy ESLint
  const paramsString = JSON.stringify(params);

  // Memoize the fetch function so we can reuse it on initial load and expiration
  const loadEmbed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 2. Parse it back into an object inside the hook.
      // Now our hook only depends on the `paramsString` primitive!
      const currentParams = JSON.parse(paramsString);
      
      const data = await getIframeEmbedConfig(resource, currentParams);
      setEmbedData(data);
    } catch (err) {
      setError(err.message || 'Failed to load embed');
    } finally {
      setLoading(false);
    }
  }, [resource, paramsString]);

  // 1. Fetch the URL when the component mounts or resource changes
  useEffect(() => {
    if (isLoggedIn) {
      loadEmbed();
    }
  }, [isLoggedIn, loadEmbed]);

  // 2. Set up the postMessage listener for iframe communication[cite: 1]
  useEffect(() => {
    const handleMessage = (event) => {
      // Hard check: Ignore messages from unknown origins[cite: 1]
      if (!embedData || event.origin !== embedData.expected_origin) return;
      if (!event.data || typeof event.data.type !== 'string') return;

      switch (event.data.type) {
        case 'embed_session_expired':
          // DO NOT reload the iframe URL, request a brand new one[cite: 1]
          console.warn('Embed session expired. Requesting a new token...');
          loadEmbed();
          break;
        case 'embed_resize':
          // Optional: Handle dynamic resizing if System B supports it[cite: 1]
          if (iframeRef.current && event.data.payload?.height) {
            iframeRef.current.style.height = `${event.data.payload.height}px`;
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [embedData, loadEmbed]);

  // Render logic
  if (!isLoggedIn) {
    return <CircularProgress />;
  }

  return (
    <>
      <Sidebar user={user} />
      
      <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" variant="h6">{error}</Typography>
            <Button variant="contained" onClick={loadEmbed} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Box>
        )}

        {!loading && !error && embedData?.iframe_url && (
          <iframe
            ref={iframeRef}
            src={embedData.iframe_url}
            title={t('modules.iframe_title')}
            // sandbox="allow-scripts allow-same-origin allow-forms"
            referrerpolicy="strict-origin-when-cross-origin"
            loading="lazy"
            style={{
              width: '100%',
              height: '100vh',
              border: 'none',
            }}
          />
        )}
      </Box>
    </>
  );
};

export default SecureEmbedPage;